/**
 * AuthGate.tsx — MSAL login wrapper.
 *
 * Wraps the app in <MsalProvider> and forces an interactive Entra login via
 * <MsalAuthenticationTemplate> (redirect flow). Children render only once an
 * account is authenticated; while interaction is in progress a loading state
 * shows; on an MSAL error the clean unauthorized/error state shows.
 *
 * NOTE: the `BackofficeUser` ROLE gate is server-side (the BFF 403s a token without it).
 * This component only establishes *authentication*; UnauthorizedScreen is also
 * rendered by the app when an API call returns 403 (authorization failure).
 */

import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { InteractionType, type IPublicClientApplication } from '@azure/msal-browser';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/Button';
import { Notice } from '../components/Notice';
import { loginRequest } from './msalConfig';

const LoadingScreen = () => {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas text-body text-ink-2">
      {t('auth.signingIn')}
    </main>
  );
};

const ErrorScreen = () => {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-canvas px-6 text-center">
      <span className="font-display text-h1 text-gold">Tucunã</span>
      <Notice
        tone="danger"
        data-testid="auth-error"
        className="max-w-md"
        action={
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t('auth.retry')}
          </Button>
        }
      >
        {t('auth.error')}
      </Notice>
    </main>
  );
};

interface AuthGateProps {
  instance: IPublicClientApplication;
  children: ReactNode;
}

export const AuthGate = ({ instance, children }: AuthGateProps) => (
  <MsalProvider instance={instance}>
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      loadingComponent={LoadingScreen}
      errorComponent={ErrorScreen}
    >
      {children}
    </MsalAuthenticationTemplate>
  </MsalProvider>
);
